/**
 * Purchase Orders API
 *
 * API client for purchase order operations
 */

import type {
  PurchaseOrder,
  CreatePurchaseOrderDto,
  UpdatePurchaseOrderDto,
  ReceivePurchaseOrderDto,
  PurchaseOrderKpis,
  PurchaseOrderStatus,
  OrderLineItem,
  Priority,
} from '@lognai/plugin-orders';
import type { PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildQueryString, type ListQueryParams } from './client';

const BASE_URL = '/api/orders/purchase-orders';

/**
 * Purchase order domain filter fields
 */
export interface PurchaseOrderFilter {
  status?: PurchaseOrderStatus;
  supplierId?: string;
  warehouseId?: string;
  priority?: Priority;
  fromDate?: Date;
  toDate?: Date;
}

/**
 * Purchase order list query parameters (pagination + filters)
 */
export type PurchaseOrderFilterParams = ListQueryParams<PurchaseOrderFilter>;

/**
 * Paginated purchase order list response
 */
export interface PaginatedPurchaseOrderList {
  items: PurchaseOrder[];
  pagination: PaginationMetadata | null;
}

/**
 * Get all purchase orders with optional filters
 */
export const listPurchaseOrders = async (
  params?: PurchaseOrderFilterParams
): Promise<PaginatedPurchaseOrderList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedPurchaseOrderList>(`${BASE_URL}${query}`);
};

/**
 * Get a single purchase order by ID
 */
export const getPurchaseOrder = async (id: string): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}`);
};

/**
 * Create a new purchase order
 */
export const createPurchaseOrder = async (
  dto: CreatePurchaseOrderDto
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Update a purchase order
 */
export const updatePurchaseOrder = async (
  id: string,
  dto: UpdatePurchaseOrderDto
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Update purchase order status
 */
export const updatePurchaseOrderStatus = async (
  id: string,
  status: PurchaseOrderStatus,
  reason?: string
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, reason }),
  });
};

/**
 * Cancel a purchase order
 */
export const cancelPurchaseOrder = async (
  id: string,
  reason?: string
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
};

/**
 * Receive goods for a purchase order
 */
export const receivePurchaseOrder = async (
  id: string,
  dto: ReceivePurchaseOrderDto
): Promise<PurchaseOrder> => {
  return apiFetch<PurchaseOrder>(`${BASE_URL}/${id}/receive`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Get purchase order line items
 */
export const getPurchaseOrderLines = async (
  id: string
): Promise<OrderLineItem[]> => {
  return apiFetch<OrderLineItem[]>(`${BASE_URL}/${id}/lines`);
};

/**
 * Get purchase order KPIs
 */
export const getPurchaseOrderKpis = async (): Promise<PurchaseOrderKpis> => {
  return apiFetch<PurchaseOrderKpis>(`${BASE_URL}/kpis`);
};
